import * as Dialog from '@radix-ui/react-dialog';
import { Button } from '@head-of-trading/ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { AccountView, HistoryItem, MissionBrief, TerminalSnapshot } from '@head-of-trading/contracts';
import { isTerminalStatus } from '@head-of-trading/contracts';
import { api, ApiError, type MissionCatalogItem } from './lib/api.js';
import { attemptSocket } from './lib/socket.js';
import { appStore, clearSession, loadSession, saveSession, useAppStore } from './store/app-store.js';
import { Intro } from './components/Intro.js';
import { Login } from './components/Access.js';
import { MissionHub } from './components/MissionHub.js';
import { Brief } from './components/Brief.js';
import { History } from './components/History.js';
import { Debrief } from './components/Debrief.js';
import { Terminal } from './components/Terminal.js';
import { appCopy } from './i18n/app.js';
import { useLanguage } from './i18n/context.js';
import { localizeBrief, localizeDebrief, localizeErrorMessage, localizeHistory, localizeMissions, localizeSnapshot } from './i18n/domain.js';

const errorText = (error: unknown): string => error instanceof Error ? error.message : 'Une erreur locale inattendue est survenue.';

function useViewportBlock(): boolean {
  const [blocked, setBlocked] = useState(() => window.innerWidth < 1_366 || window.innerHeight < 768);
  useEffect(() => { const update = () => setBlocked(window.innerWidth < 1_366 || window.innerHeight < 768); window.addEventListener('resize', update); return () => window.removeEventListener('resize', update); }, []);
  return blocked;
}

export function App() {
  const state = useAppStore((value) => value);
  const tooSmall = useViewportBlock();
  const { language } = useLanguage();
  const copy = appCopy[language];
  const localizedMissions = useMemo(() => localizeMissions(state.missions, language), [state.missions, language]);
  const localizedBrief = useMemo(() => state.brief ? localizeBrief(state.brief, language) : null, [state.brief, language]);
  const localizedSnapshot = useMemo(() => state.snapshot ? localizeSnapshot(state.snapshot, language) : null, [state.snapshot, language]);
  const localizedDebrief = useMemo(() => state.debrief ? localizeDebrief(state.debrief, language) : null, [state.debrief, language]);
  const localizedHistory = useMemo(() => localizeHistory(state.history, language), [state.history, language]);
  const displayedError = state.error ? localizeErrorMessage(state.error, language) : null;

  const loadMissions = useCallback(async (account: AccountView) => {
    appStore.set({ busy: true, error: null });
    try {
      const missions = await api.missions();
      appStore.set({ missions, account, screen: 'missions', busy: false });
      saveSession(account, account.activeAttemptId);
    } catch (error) { appStore.set({ busy: false, error: errorText(error) }); }
  }, []);

  useEffect(() => {
    const session = loadSession();
    if (!session) return;
    appStore.set({ account: session.account, introVisible: false });
    void (async () => {
      if (session.attemptId) {
        try {
          const snapshot = await api.attempt(session.attemptId);
          if (!isTerminalStatus(snapshot.status)) {
            appStore.resetTerminalUi(snapshot); attemptSocket.connect(snapshot.attemptId); return;
          }
        } catch { /* stale session falls back to mission hub */ }
      }
      await loadMissions(session.account);
    })();
    return () => attemptSocket.close();
  }, [loadMissions]);

  const openAccount = async (pseudo: string) => {
    appStore.set({ busy: true, error: null });
    try {
      const response = await api.openAccount(pseudo);
      if (response.takeoverRequired) { appStore.set({ busy: false, takeoverAccount: response.account }); return; }
      await loadMissions(response.account);
    } catch (error) {
      const conflict = error instanceof ApiError && error.status === 409 && error.body && typeof error.body === 'object' ? (error.body as { account?: AccountView }).account : undefined;
      if (conflict) appStore.set({ busy: false, takeoverAccount: conflict }); else appStore.set({ busy: false, error: errorText(error) });
    }
  };
  const takeover = async () => {
    if (!state.takeoverAccount) return;
    appStore.set({ busy: true, error: null });
    try { const account = await api.takeover(state.takeoverAccount.accountId); appStore.set({ takeoverAccount: null }); await loadMissions(account); } catch (error) { appStore.set({ busy: false, error: errorText(error) }); }
  };
  const changeAccount = () => {
    attemptSocket.close();
    clearSession();
    appStore.set({
      account: null,
      screen: 'login',
      missions: [],
      selectedMission: null,
      brief: null,
      attemptId: null,
      snapshot: null,
      debrief: null,
      history: [],
      takeoverAccount: null,
      controlRevoked: false,
      busy: false,
      error: null
    });
  };
  const selectMission = (mission: MissionCatalogItem) => { if (!mission.brief) return; appStore.set({ selectedMission: mission, brief: mission.brief, screen: 'brief', error: null }); };
  const launch = async () => {
    if (!state.selectedMission || tooSmall) return;
    appStore.set({ busy: true, error: null });
    try {
      const result = await api.createAttempt(state.selectedMission.missionId);
      const snapshot = result.snapshot ?? await api.attempt(result.attemptId);
      const account = state.account ? { ...state.account, hasActiveAttempt: true, activeAttemptId: result.attemptId } : null;
      if (account) saveSession(account, result.attemptId);
      appStore.set({ account, busy: false }); appStore.resetTerminalUi(snapshot); attemptSocket.connect(result.attemptId);
    } catch (error) { appStore.set({ busy: false, error: errorText(error) }); }
  };
  const resume = async () => {
    const id = state.account?.activeAttemptId; if (!id || tooSmall) return;
    appStore.set({ busy: true, error: null });
    try { const snapshot = await api.attempt(id); appStore.set({ brief: state.missions.find((mission) => mission.missionId === snapshot.missionId)?.brief ?? null, busy: false }); appStore.resetTerminalUi(snapshot); attemptSocket.connect(id); } catch (error) { appStore.set({ busy: false, error: errorText(error) }); }
  };
  const abandon = async () => {
    const id = state.attemptId ?? state.account?.activeAttemptId; if (!id) return;
    appStore.set({ busy: true, error: null });
    try {
      attemptSocket.close(); await api.abandon(id);
      const account = state.account ? { ...state.account, hasActiveAttempt: false, activeAttemptId: null } : null;
      if (account) await loadMissions(account); else appStore.set({ screen: 'login', busy: false });
    } catch (error) { appStore.set({ busy: false, error: errorText(error) }); }
  };
  const showHistory = async () => {
    if (!state.account) return; appStore.set({ busy: true, error: null });
    try { const history = await api.history(state.account.accountId); appStore.set({ history, busy: false, screen: 'history' }); } catch (error) { appStore.set({ busy: false, error: errorText(error) }); }
  };
  const showDebrief = useCallback(async (attemptId: string) => {
    try { const debrief = await api.debrief(attemptId); attemptSocket.close(); appStore.set({ debrief, screen: 'debrief', busy: false, error: null }); const account = appStore.getState().account; if (account) saveSession({ ...account, hasActiveAttempt: false, activeAttemptId: null }, null); } catch (error) { appStore.set({ error: errorText(error), busy: false }); }
  }, []);
  const terminalFinish = useCallback(() => { const snapshot = appStore.getState().snapshot; if (snapshot && isTerminalStatus(snapshot.status)) void showDebrief(snapshot.attemptId); }, [showDebrief]);
  const openHistoryItem = (item: HistoryItem) => void showDebrief(item.attemptId);
  const missions = () => { const account = state.account; if (account) void loadMissions({ ...account, hasActiveAttempt: false, activeAttemptId: null }); };
  const replay = () => { const mission = state.missions.find((item) => item.missionId === state.debrief?.missionId); if (mission) { selectMission(mission); } else missions(); };

  let content: React.ReactNode;
  if (state.screen === 'login' || !state.account) content = <Login busy={state.busy} error={displayedError} takeover={state.takeoverAccount} onSubmit={openAccount} onTakeover={takeover} onCancelTakeover={() => appStore.set({ takeoverAccount: null })} />;
  else if (state.screen === 'missions') content = <MissionHub account={state.account} missions={localizedMissions} busy={state.busy} tooSmall={tooSmall} onSelect={(mission) => { const source = state.missions.find((item) => item.missionId === mission.missionId); if (source) selectMission(source); }} onResume={() => void resume()} onAbandon={() => void abandon()} onHistory={() => void showHistory()} onChangeAccount={changeAccount} />;
  else if (state.screen === 'brief' && localizedBrief) content = <Brief brief={localizedBrief} busy={state.busy} tooSmall={tooSmall} onBack={() => appStore.set({ screen: 'missions' })} onLaunch={() => void launch()} />;
  else if (state.screen === 'terminal' && localizedSnapshot) content = <Terminal snapshot={localizedSnapshot} onExit={() => void abandon()} onFinish={terminalFinish} />;
  else if (state.screen === 'debrief' && localizedDebrief) content = <Debrief value={localizedDebrief} onMissions={missions} onHistory={() => void showHistory()} onReplay={replay} />;
  else if (state.screen === 'history') content = <History account={state.account} items={localizedHistory} onBack={() => appStore.set({ screen: 'missions' })} onOpen={openHistoryItem} />;
  else content = <main className="loading-screen"><span className="loader" /><p>{copy.loading}</p></main>;

  return (
    <div data-testid="app-shell" className="app-shell">
      {content}
      {state.introVisible && <Intro onDone={() => appStore.set({ introVisible: false })} />}
      {state.controlRevoked && <Dialog.Root open><Dialog.Portal><Dialog.Overlay className="dialog-overlay" /><Dialog.Content className="dialog dialog--compact"><Dialog.Title>{copy.controlTakenTitle}</Dialog.Title><Dialog.Description>{copy.controlTakenDescription}</Dialog.Description><div className="dialog-actions"><Button variant="primary" onClick={changeAccount}>{copy.returnToAccess}</Button></div></Dialog.Content></Dialog.Portal></Dialog.Root>}
      {displayedError && state.screen !== 'login' && <div className="global-error" role="alert"><span>{displayedError}</span><button aria-label={copy.closeError} onClick={() => appStore.set({ error: null })}>×</button></div>}
    </div>
  );
}
